import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AppHeader,
    DataList,
    Format,
    ListItemElement,
    RoundButton, Searchbox,
    Spinner,
    Tooltip
} from "@tag/tag-components-react-v4";
import {
    ArrowRewindLined,
    ClockFilled,
    DeleteFilled,
    DeleteLined,
    DesktopFilled,
    SearchFilled,
    UserMaleLined
} from "@tag/tag-icons";
import TerminationsService from "../services/TerminationsService";
import {ACCTermination} from "../types/ACCTermination";
import styles from "../CustomerList/CustomerList.module.scss";
import AppRolesService from "../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import HelperMethods from "../../General/services/HelperMethods";
import UnlockSiteModal from "./UnlockSiteModal";
import DeleteSiteModal from "./DeleteSiteModal";
import { ProjectAccents } from "../../General/config/projectAccents";

function Terminations() {

    const [allTerminations, setAllTerminations] = useState<ACCTermination[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedTermination, setSelectedTermination] = useState<ACCTermination|undefined>();
    const [deleteTermination, setDeleteTermination] = useState<ACCTermination|undefined>();
    const [alert, setAlert] = useState<{type: any, message: string}>();

    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    let canUnlock = AppRolesService.canUserLockAccEnvironments(accounts, isAuthenticated);
    let canDelete = AppRolesService.isUserAdmin(accounts, isAuthenticated);

    useEffect(() => {
        loadTerminationsData()
    }, []);

    const loadTerminationsData = () => {
        setLoading(true);
        TerminationsService.getTerminations().then(n => {
            const updatedList = n.map((item: ACCTermination) => {
                return {
                    ...item,
                    initials: HelperMethods.getInitials(item.clientName),
                    primaryFieldValue: `${item.clientName} (${item.clientID} - ${item.appGroupName})`
                };
            });
            setAllTerminations(updatedList);
            setLoading(false);
        })
    }

    const getTerminationsListData = (): ACCTermination[] => {
        if (!searchTerm) {
            return allTerminations;
        }

        return HelperMethods.advancedFuseSearch(searchTerm, allTerminations, ['clientID', 'clientName', 'appGroupName']);
    }

    const terminationsData = useMemo(
        () => getTerminationsListData(),
        [searchTerm, allTerminations]
    );

    let content;
    if (!allTerminations && loading) {
        content = <Spinner full/>
    }
    else if (!allTerminations.length) {
        content = <>
            <h3>There are no terminations</h3>
        </>
    }
    else {
        content = <>
            <h1>Terminations</h1>
            <h4>Sites will be deleted after 28 days from the date they were locked</h4>
            <AppHeader
                heading="Customer List"
                icon={<SearchFilled/>}
                accent={ProjectAccents.CareCompliance}
            >
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Searchbox
                        onSearch={(e) => setSearchTerm(e)}
                        clearButton
                        style={{width: '100%'}}
                    />
                </div>
            </AppHeader>
            <DataList
                avatarProps={{srcField: 'image', placeholderField: 'initials'}}
                primaryField="primaryFieldValue"
                renderSubItemElements={(dataItem) => (
                    <>
                        <ListItemElement icon={<UserMaleLined accent={ProjectAccents.CareCompliance} />} label={'Locked by:'}>{dataItem.lockedBy}</ListItemElement>
                        {
                            dataItem.reason ?
                            <ListItemElement icon={<DesktopFilled accent={ProjectAccents.CareCompliance} />} label={'Reason: '}>{dataItem.reason}</ListItemElement> : undefined
                        }
                        {
                            dataItem.lockedTimestamp ?
                            <ListItemElement icon={<ClockFilled accent={ProjectAccents.CareCompliance}/>} label="Locked at:">
                                <Format value={dataItem.lockedTimestamp} dataType="datetime"/>
                            </ListItemElement> : undefined
                        }
                        {
                            dataItem.deletedTimestamp ?
                                <ListItemElement icon={<DeleteFilled accent={ProjectAccents.CareCompliance}/>} label="Deleted on:">
                                    <Format value={dataItem.deletedTimestamp} dataType="datetime"/>
                                </ListItemElement> : undefined
                        }
                        {
                            dataItem.deletedBy ?
                                <ListItemElement icon={<UserMaleLined accent={ProjectAccents.CareCompliance}/>} label="Deleted by:">{dataItem.deletedBy}</ListItemElement> : undefined
                        }
                    </>
                )}
                renderRightElements={(dataItem) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {
                            canUnlock ?
                            <Tooltip
                                background={'dark'}
                                placement={'left'}
                                tooltipFor={
                                    <RoundButton
                                        disabled={!!dataItem.deletedTimestamp}
                                        onClick={() => setSelectedTermination(dataItem)}
                                        icon={<ArrowRewindLined />}
                                    />
                                }
                            >
                                {
                                    !dataItem.deletedTimestamp
                                        ? "Unlock site"
                                        : "Site already deleted"
                                }
                            </Tooltip> : <></>
                        }
                        {
                            canDelete ?
                            <Tooltip
                                background={'dark'}
                                placement={'left'}
                                tooltipFor={
                                    <RoundButton
                                        disabled={!!dataItem.deletedTimestamp}
                                        onClick={() => setDeleteTermination(dataItem)}
                                        icon={<DeleteLined />}
                                    />
                                }
                            >
                                {
                                    !dataItem.deletedTimestamp
                                        ? "Delete site"
                                        : "Site already deleted"
                                }
                            </Tooltip> : <></>
                        }
                    </div>
                )}
                data={terminationsData}
            />
        </>
    }

    return (
        <>
            {content}

            {selectedTermination ?
                <UnlockSiteModal
                    visible={!!selectedTermination}
                    selectedTermination={selectedTermination}
                    handleClose={() => setSelectedTermination(undefined)}
                    setAlert={setAlert}
                />:undefined
            }

            {deleteTermination ?
                <DeleteSiteModal
                    visible={!!deleteTermination}
                    selectedTermination={deleteTermination}
                    handleClose={() => setDeleteTermination(undefined)}
                    setAlert={setAlert}
                />:undefined
            }

            {alert ?
                <Alert
                    className={styles.CustomerListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }

            {loading ? <Spinner full /> : undefined}
        </>
    )

}

export default Terminations;
